// src/components/Transactions.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { FaFileExcel } from 'react-icons/fa';
import BranchSelector from './BranchSelector';
import ReactDOM from 'react-dom/client';

const API_URL = '/api';

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

const transliterateToArabic = (englishText) => {
  if (!englishText || englishText === 'Guest') return 'ضيف';
  const text = englishText.toLowerCase().trim();
  const specialMap = {
    'sh': 'ش', 'ch': 'تش', 'th': 'ث', 'kh': 'خ', 'gh': 'غ',
    'dh': 'ذ', 'zh': 'ژ', 'ph': 'ف', 'ng': 'نغ', 'nk': 'نك',
    'nd': 'ند', 'nt': 'نت', 'mp': 'مب', 'mb': 'مب'
  };
  const letterMap = {
    'a': 'ا', 'b': 'ب', 'c': 'ك', 'd': 'د', 'e': 'ي', 'f': 'ف',
    'g': 'ج', 'h': 'ه', 'i': 'ي', 'j': 'ج', 'k': 'ك', 'l': 'ل',
    'm': 'م', 'n': 'ن', 'o': 'و', 'p': 'ب', 'q': 'ق', 'r': 'ر',
    's': 'س', 't': 'ت', 'u': 'و', 'v': 'ف', 'w': 'و', 'x': 'كس',
    'y': 'ي', 'z': 'ز'
  };
  let result = '';
  let i = 0;
  while (i < text.length) {
    let matched = false;
    for (let len = 3; len >= 2; len--) {
      if (i + len <= text.length) {
        const sub = text.substring(i, i + len);
        if (specialMap[sub]) {
          result += specialMap[sub];
          i += len;
          matched = true;
          break;
        }
      }
    }
    if (!matched) {
      const char = text[i];
      result += letterMap[char] || char;
      i++;
    }
  }
  return result;
};

const getValueOrNA = (value) => {
  return value && value.trim() !== '' ? value : 'Not available';
};

const getStatusStyle = (status) => {
  switch(status) {
    case 'Delivery Pending': return { bg: '#fef3c7', color: '#92400e' };
    case 'Out for Delivery': return { bg: '#ede9fe', color: '#5b21b6' };
    case 'Delivered': return { bg: '#d1fae5', color: '#065f46' };
    case 'Completed': return { bg: '#d1fae5', color: '#065f46' };
    case 'Cancelled': return { bg: '#fee2e2', color: '#991b1b' };
    default: return { bg: '#f1f5f9', color: '#475569' };
  }
};

// ============================================================
// COMPONENT: ThermalBillPrint
// ============================================================

const ThermalBillPrint = React.forwardRef(({ bill, branch, vatRate, deliveryType }, ref) => {
  const subtotal = Number(bill.subtotal || bill.total_amount || 0);
  const actualVatRate = bill.vat_rate || vatRate || 5;
  let vatAmount = Number(bill.vat || bill.vat_amount || 0);
  let total = Number(bill.final_amount || bill.grand_total || 0);
  
  if (vatAmount === 0 && subtotal > 0) {
    vatAmount = subtotal * (actualVatRate / 100);
    total = subtotal + vatAmount;
  }
  
  const date = new Date(bill.created_at || Date.now()).toLocaleString();
//const logoUrl = '/nursery/logo.png'; 
 const logoUrl = 'https://nasmatalzahour.com/nursery/logo.png';
//const logoUrl = window.location.origin + '/nursery/logo.png';
  const items = bill.items || bill.bill_items || [];

  const customerNameEnglish = bill.customer_name || 'Guest';
  const customerNameArabic = transliterateToArabic(customerNameEnglish);
  const trnNumber = branch?.trn_no || 'TRN: 100220990400003';

  return (
    <div ref={ref} className="thermal-printable-area" style={{
      fontFamily: "monospace", 
      width: '70mm',
      margin: '0 auto',
      padding: '0px 2px',
      fontSize: '12px', 
      fontWeight: 'normal',
      lineHeight: '1.3',
      letterSpacing: '0px', 
      boxSizing: 'border-box',
      color: '#000000'
    }}>
      
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        marginBottom: '8px', 
        paddingBottom: '8px', 
        borderBottom: '1px dashed #000000',
        gap: '10px'
      }}>
        <img 
          src={logoUrl} 
          alt="Logo" 
          style={{ width: '45px', height: 'auto', filter: 'grayscale(100%)' }} 
          onError={(e) => e.target.style.display = 'none'} 
        />
        
        <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', lineHeight: '1.1' }}>NASMAT</div>
          <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', lineHeight: '1.1' }}>AL ZOHOUR</div>
          <div style={{ fontSize: '9px', color: '#000000', fontWeight: 'normal', lineHeight: '1.2', marginTop: '2px', maxWidth: '180px', wordBreak: 'break-word' }}>
            {branch?.address || ''}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '13px', marginBottom: '4px' }}>TAX INVOICE</div>
      
      <div style={{ textAlign: 'center', fontSize: '11px', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px dotted #ccc' }}>
        <span style={{ fontWeight: 'bold' }}>📞</span> +971552698951 / +971526629183
      </div>

      <div style={{ marginBottom: '8px', fontSize: '11px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <span>Bill No: {bill.bill_number}</span>
          <span>Date: {date.split(',')[0]}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '2px' }}>
          <span>TRN: {trnNumber.replace('TRN: ', '')}</span>
        </div>
        
        <div style={{ marginTop: '2px' }}>
          <span>Customer: </span>
          <span style={{ fontWeight: 'bold' }}>
            {customerNameEnglish}
            {customerNameArabic && ` / ${customerNameArabic}`}
          </span>
        </div>
        
        {bill.customer_phone && <div>Phone: {bill.customer_phone}</div>}
        {bill.customer_address && <div>Address: {bill.customer_address.substring(0, 30)}</div>}
        <div>Payment: {bill.payment_mode}</div>
        <div>Delivery: {bill.delivery_type === 'delivery' ? 'Home Delivery' : 'Walk-in'}</div>
      </div>

      <div style={{ display: 'table', width: '100%', borderTop: '1px solid #000000', borderBottom: '1px solid #000000', padding: '4px 0', fontWeight: 'bold' }}>
        <div style={{ display: 'table-cell', width: '42%', textAlign: 'left' }}>Item</div>
        <div style={{ display: 'table-cell', width: '15%', textAlign: 'center' }}>Qty</div>
        <div style={{ display: 'table-cell', width: '20%', textAlign: 'right' }}>Rate</div>
        <div style={{ display: 'table-cell', width: '23%', textAlign: 'right' }}>Total</div>
      </div>

      {items && items.map((item, idx) => {
        const itemPriceWithVAT = Number(item.price) || Number(item.unit_price) || 0;
        const itemQty = Number(item.quantity) || 1;
        const itemTotalWithVAT = Number(item.subtotal) || (itemPriceWithVAT * itemQty);
        
        return (
          <div key={idx} style={{ display: 'table', width: '100%', padding: '4px 0', borderBottom: '1px dotted #ccc' }}>
            <div style={{ display: 'table-cell', width: '42%', textAlign: 'left', wordBreak: 'break-word', paddingRight: '2px' }}>
              {item.name || item.product_name}
            </div>
            <div style={{ display: 'table-cell', width: '15%', textAlign: 'center', verticalAlign: 'top' }}>{itemQty}</div>
            <div style={{ display: 'table-cell', width: '20%', textAlign: 'right', verticalAlign: 'top' }}>{itemPriceWithVAT.toFixed(2)}</div>
            <div style={{ display: 'table-cell', width: '23%', textAlign: 'right', verticalAlign: 'top' }}>{itemTotalWithVAT.toFixed(2)}</div>
          </div>
        );
      })}

      <div style={{ marginTop: '6px', paddingTop: '4px', borderTop: '1px solid #000000' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Subtotal (excl. VAT):</span>
          <span>{subtotal.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>VAT ({actualVatRate}%):</span>
          <span>{vatAmount.toFixed(2)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #000000' }}>
          <span>TOTAL:</span>
          <span>{total.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '12px', paddingTop: '8px', borderTop: '1px dashed #000000' }}>
        <div style={{ fontWeight: 'bold' }}>THANK YOU! VISIT AGAIN</div>
        <div style={{ fontSize: '9px', marginTop: '2px' }}>Quality is what we believe in</div>
      </div>
    </div>
  );
});

// ============================================================
// COMPONENT: A4BillPrint
// ============================================================

const A4BillPrint = React.forwardRef(({ bill, branch, vatRate, deliveryType }, ref) => {
  const subtotal = Number(bill.subtotal || bill.total_amount || 0);
  let vatAmount = Number(bill.vat || bill.vat_amount || 0);
  let total = Number(bill.final_amount || bill.grand_total || 0);
  const actualVatRate = bill.vat_rate || vatRate || 5;
  
  if (vatAmount === 0 && subtotal > 0) {
    vatAmount = subtotal * (actualVatRate / 100);
    total = subtotal + vatAmount;
  }
  const logoUrl = 'https://nasmatalzahour.com/nursery/logo.png';
//const logoUrl = window.location.origin + '/logo.png';
  const branchEmail = branch?.email || 'NasmatAlzohour@gmail.com';
  const branchPhone1 = '+971 552698951';
  const branchPhone2 = branch?.phone2 || '+971 526629183';
  const branchTrn = branch?.trn_no || 'TRN: 100220990400003';
  const items = bill.items || bill.bill_items || [];

  const customerNameEnglish = bill.customer_name || 'Guest';
  const customerNameArabic = transliterateToArabic(customerNameEnglish);

  const formattedDate = bill.created_at 
    ? new Date(bill.created_at).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    : new Date().toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });

  return (
    <div ref={ref} style={{
      maxWidth: '800px',
      margin: '0 auto',
      background: 'white',
      fontFamily: "'Book Antiqua', 'Times New Roman', serif",
      padding: '30px',
      boxShadow: '0 0 10px rgba(0,0,0,0.1)'
    }}>
      <div style={{ 
        marginBottom: '25px', 
        paddingBottom: '20px', 
        borderBottom: '2px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
          <div style={{ textAlign: 'center' }}>
            <img src={logoUrl} alt="Logo" style={{ width: '70px', height: 'auto' }} onError={(e) => e.target.style.display = 'none'} />
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: '800', color: '#2c4a3e', marginTop: '10px', textAlign: 'center' }}>NASMAT</div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#c9a03d', marginTop: '10px' }}>AL ZOHOUR</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#000', letterSpacing: '2px', marginBottom: '10px' }}>TAX INVOICE</div>
          <div style={{ fontSize: '11px', color: '#64748b', lineHeight: '1.5' }}>
            <div>{branch?.address || ''}</div>
            <div>📞 {branchPhone1}/{branchPhone2}</div>
            <div>Email: {branchEmail}</div>
            {branchTrn && <div>{branchTrn}</div>}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '30px', gap: '30px' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', color: '#1e293b' }}>Bill To</div>
          <div style={{ fontSize: '12px', color: '#475569' }}>
            <div><strong>
              {getValueOrNA(customerNameEnglish)}
              {customerNameArabic && ` / ${customerNameArabic}`}
            </strong></div>
            <div>Attn: {getValueOrNA(customerNameEnglish)}</div>
            <div>{bill.customer_address ? bill.customer_address.split(',')[0] : 'Client'}</div>
            {bill.customer_address && bill.customer_address.split(',')[1] && <div>{bill.customer_address.split(',')[1]}</div>}
            {bill.customer_phone && <div>Phone: {bill.customer_phone}</div>}
            {bill.customer_email && <div>Email: {bill.customer_email}</div>}
          </div>
        </div>
        <div style={{ minWidth: '250px', textAlign: 'right' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '10px', fontSize: '14px', color: '#1e293b', textAlign: 'right' }}>Invoice Details</div>
          <div style={{ fontSize: '12px', lineHeight: '1.8', color: '#334155', textAlign: 'right' }}>
            <div><strong>Invoice No:</strong> {bill.bill_number || 'N/A'}</div>
            <div><strong>Date:</strong> {formattedDate}</div>
            {bill.delivery_type && <div><strong>Delivery Type:</strong> {bill.delivery_type === 'delivery' ? ' Home Delivery' : ' Walk-in'}</div>}
          </div>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '25px', fontSize: '12px' }}>
        <thead>
          <tr style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ padding: '10px', textAlign: 'left', width: '45%' }}>Description</th>
            <th style={{ padding: '10px', textAlign: 'center', width: '10%' }}>Qty</th>
            <th style={{ padding: '10px', textAlign: 'right', width: '20%' }}>Rate (AED incl. VAT)</th>
            <th style={{ padding: '10px', textAlign: 'right', width: '20%' }}>Total (AED)</th>
          </tr>
        </thead>
        <tbody>
          {items && items.length > 0 ? (
            items.map((item, idx) => {
              const itemPriceExclVAT = Number(item.unit_price || item.price || 0);
              const itemQty = Number(item.quantity || 1);
              const itemPriceWithVAT = itemPriceExclVAT * (1 + actualVatRate / 100);
              const itemTotalWithVAT = itemPriceWithVAT * itemQty;
              
              return (
                <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: '10px' }}>
                    <strong>{item.name || item.product_name}</strong>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>{itemQty}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>{itemPriceWithVAT.toFixed(2)}</td>
                  <td style={{ padding: '10px', textAlign: 'right', fontWeight: '500' }}>{itemTotalWithVAT.toFixed(2)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="4" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>No items</td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{ marginTop: '10px', paddingTop: '15px' }}>
        <div style={{ marginBottom: '5px', fontSize: '12px', textAlign: 'right' }}>
          Subtotal (excl. VAT): AED {subtotal.toFixed(2)}
        </div>
        <div style={{ marginBottom: '5px', fontSize: '12px', textAlign: 'right' }}>
          VAT ({actualVatRate}%): AED {vatAmount.toFixed(2)}
        </div>
        <div style={{ 
          marginTop: '10px', 
          paddingTop: '10px',
          borderTop: '2px solid #1e293b',
          fontSize: '16px',
          fontWeight: 'bold',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Payment Mode: {bill.payment_mode || 'Cash'}</span>
          <span>TOTAL AMOUNT: AED {total.toFixed(2)}</span>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '10px', paddingTop: '10px' }}>
        <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '14px' }}>THANK YOU FOR SHOPPING WITH US!</p>
        <p style={{ fontSize: '10px', color: '#64748b', margin: '5px 0' }}>Quality is what we believe in</p>
      </div>
    </div>
  );
});

// ============================================================
// COMPONENT: StatsBar
// ============================================================

const StatsBar = ({ branch, totalInvoices }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      padding: '10px 0',
      borderBottom: '1px solid #e2e8f0',
      flexWrap: 'wrap',
      gap: '10px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: '13px', color: '#475569' }}>
          🏢 Branch: <strong>{branch?.branch_name || '-'}</strong>
        </span>
        <span style={{ color: '#cbd5e1' }}>|</span>
        <span style={{ fontSize: '13px', color: '#475569' }}>
          📊 Total Invoices: <strong>{totalInvoices}</strong>
        </span>
      </div>
      <div style={{ fontSize: '11px', color: '#8a99b0' }}>
        <span>© 2024 NASMAT AL ZOHOUR v2.0.0</span>
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: FilterDropdown
// ============================================================

const FilterDropdown = ({ 
  icon, 
  label, 
  options, 
  value, 
  onChange, 
  dropdownRef,
  showDropdown,
  setShowDropdown
}) => {
  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
      <button
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 14px',
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          fontSize: '13px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          color: '#1e293b',
          cursor: 'pointer',
          minWidth: '130px',
          justifyContent: 'space-between'
        }}
        onClick={() => setShowDropdown(!showDropdown)}
        type="button"
      >
        <span>{icon}</span>
        <span>{options.find(o => o.value === value)?.label}</span>
        <span>▼</span>
      </button>
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 5px)',
          left: 0,
          minWidth: '180px',
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          overflow: 'hidden'
        }}>
          {options.map(option => (
            <div
              key={option.value}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                fontSize: '13px',
                fontFamily: "'Book Antiqua', 'Times New Roman', serif",
                color: '#1e293b',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: value === option.value ? '#e8f5e9' : 'transparent',
                fontWeight: value === option.value ? 500 : 400
              }}
              onClick={() => { onChange(option.value); setShowDropdown(false); }}
              onMouseEnter={(e) => e.currentTarget.style.background = value === option.value ? '#e8f5e9' : '#f8fafc'}
              onMouseLeave={(e) => e.currentTarget.style.background = value === option.value ? '#e8f5e9' : 'transparent'}
            >
              <span>{option.label}</span>
              {value === option.value && <span style={{ marginLeft: 'auto', color: '#475569', fontWeight: 'bold' }}>✓</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// COMPONENT: CustomDateRangePicker
// ============================================================

const CustomDateRangePicker = ({ from, to, onFromChange, onToChange }) => {
  return (
    <div style={{
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      padding: '4px 10px',
      background: '#fff',
      borderRadius: '10px',
      border: '1px solid #e2e8f0'
    }}>
      <input
        type="date"
        style={{
          padding: '6px 8px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '13px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          background: '#fff'
        }}
        value={from}
        onChange={(e) => onFromChange(e.target.value)}
      />
      <span>→</span>
      <input
        type="date"
        style={{
          padding: '6px 8px',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          fontSize: '13px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          background: '#fff'
        }}
        value={to}
        onChange={(e) => onToChange(e.target.value)}
      />
    </div>
  );
};

// ============================================================
// COMPONENT: SearchBar
// ============================================================

const SearchBar = ({ searchTerm, setSearchTerm }) => {
  return (
    <div style={{
      flex: 1,
      minWidth: '260px',
      position: 'relative'
    }}>
      <span style={{
        position: 'absolute',
        left: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '14px',
        opacity: 0.6
      }}>🔍</span>
      <input
        type="text"
        style={{
          width: '100%',
          padding: '10px 35px 10px 35px',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          fontSize: '14px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          outline: 'none',
          background: '#fff'
        }}
        placeholder="Search by invoice, customer name, or phone number..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      {searchTerm && (
        <button
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: 'translateY(-50%)',
            background: '#e2e8f0',
            border: 'none',
            borderRadius: '50%',
            width: '20px',
            height: '20px',
            cursor: 'pointer',
            fontSize: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b'
          }}
          onClick={() => setSearchTerm('')}
        >✕</button>
      )}
    </div>
  );
};

// ============================================================
// COMPONENT: TransactionRow
// ============================================================

const TransactionRow = ({ 
  bill, 
  onPrintThermal, 
  onPrintA4, 
  onStatusChange, 
  updatingStatus,
  getStatusStyle
}) => {
  const statusStyle = getStatusStyle(bill.delivery_status);
  
  return (
    <tr style={{
      borderBottom: '1px solid #f0f2f5',
      transition: 'background 0.2s'
    }}>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <span style={{ fontFamily: 'monospace', fontWeight: 500, color: '#475569' }}>{bill.bill_number}</span>
      </td>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <div>{new Date(bill.created_at).toLocaleDateString()}</div>
        <div style={{ fontSize: '12px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#8a99b0', marginTop: '4px' }}>
          {new Date(bill.created_at).toLocaleTimeString()}
        </div>
      </td>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <div>{bill.customer_name}</div>
        {bill.customer_phone && (
          <div style={{ fontSize: '11px', color: '#8a99b0' }}> {bill.customer_phone}</div>
        )}
      </td>
      <td style={{ 
        padding: '14px 16px', 
        fontSize: '14px', 
        fontFamily: "'Book Antiqua', 'Times New Roman', serif", 
        color: '#1f2a3e',
        textAlign: 'right',
        fontWeight: 600,
        color: '#475569'
      }}>
        AED {Number(bill.final_amount).toFixed(2)}
      </td>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <span style={{
          display: 'inline-flex',
          padding: '4px 12px',
          borderRadius: '20px',
          fontSize: '12px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          fontWeight: 500,
          background: bill.payment_mode === 'Cash' ? '#e8f5e9' : bill.payment_mode === 'Card' ? '#e3f2fd' : '#fff3e0',
          color: bill.payment_mode === 'Cash' ? '#475569' : bill.payment_mode === 'Card' ? '#1565c0' : '#e65100'
        }}>
          {bill.payment_mode}
        </span>
      </td>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          padding: '4px 10px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '500',
          background: bill.delivery_type === 'delivery' ? '#e0f2fe' : '#f1f5f9',
          color: bill.delivery_type === 'delivery' ? '#0369a1' : '#475569'
        }}>
          {bill.delivery_type === 'delivery' ? '🚚 Delivery' : '🏪 Walk-in'}
        </span>
      </td>
      <td style={{ padding: '14px 16px', fontSize: '14px', fontFamily: "'Book Antiqua', 'Times New Roman', serif", color: '#1f2a3e' }}>
        <select
          value={bill.delivery_status || 'Delivery Pending'}
          onChange={(e) => onStatusChange(bill.bill_id, e.target.value)}
          disabled={updatingStatus}
          style={{
            width: '150px',
            padding: '6px 8px',
            borderRadius: '20px',
            border: 'none',
            fontSize: '12px',
            fontWeight: '500',
            fontFamily: "'Book Antiqua', 'Times New Roman', serif",
            cursor: 'pointer',
            outline: 'none',
            textAlign: 'left',
            display: 'inline-block',
            background: statusStyle.bg,
            color: statusStyle.color
          }}
        >
          <option value="Delivery Pending">⏳ Delivery Pending</option>
          <option value="Out for Delivery">🚚 Out for Delivery</option>
          <option value="Delivered">✅ Delivered</option>
          <option value="Completed">🏪 Completed</option>
          <option value="Cancelled">❌ Cancelled</option>
        </select>
      </td>
      <td style={{ 
        padding: '14px 16px', 
        fontSize: '14px', 
        fontFamily: "'Book Antiqua', 'Times New Roman', serif", 
        color: '#1f2a3e',
        textAlign: 'center',
        whiteSpace: 'nowrap'
      }}>
        <button
          style={{
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '8px',
            transition: 'all 0.2s',
            marginRight: '8px'
          }}
          onClick={() => onPrintThermal(bill)}
          disabled={updatingStatus}
          title="Print 80mm Thermal"
        >🧾</button>
        <button
          style={{
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            fontSize: '16px',
            cursor: 'pointer',
            padding: '6px 12px',
            borderRadius: '8px',
            transition: 'all 0.2s'
          }}
          onClick={() => onPrintA4(bill)}
          disabled={updatingStatus}
          title="Print A4"
        >📄</button>
      </td>
    </tr>
  );
};

// ============================================================
// COMPONENT: Pagination
// ============================================================

const Pagination = ({
  currentPage,
  totalPages,
  itemsPerPage,
  itemsPerPageOptions,
  onPageChange,
  onItemsPerPageChange,
  onPrevious,
  onNext,
  getPageNumbers
}) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 20px',
      borderTop: '1px solid #e9edf2',
      flexWrap: 'wrap',
      gap: '12px',
      background: '#fafcfc'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ fontSize: '13px', color: '#5b677b', fontFamily: "'Book Antiqua', 'Times New Roman', serif" }}>Show:</label>
        <select
          value={itemsPerPage}
          onChange={onItemsPerPageChange}
          style={{
            padding: '6px 10px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '13px',
            fontFamily: "'Book Antiqua', 'Times New Roman', serif",
            background: '#fff',
            cursor: 'pointer'
          }}
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </div>
      
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={onPrevious}
          disabled={currentPage === 1}
          style={{
            padding: '6px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: '#fff',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontFamily: "'Book Antiqua', 'Times New Roman', serif",
            color: '#475569',
            transition: 'all 0.2s',
            opacity: currentPage === 1 ? 0.5 : 1
          }}
        >&laquo; Previous</button>
        
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={page === '...'}
            style={{
              padding: '6px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              background: currentPage === page ? '#475569' : '#fff',
              cursor: page === '...' ? 'default' : 'pointer',
              fontSize: '13px',
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: currentPage === page ? '#fff' : '#475569',
              transition: 'all 0.2s',
              borderColor: currentPage === page ? '#475569' : '#e2e8f0'
            }}
          >{page}</button>
        ))}
        
        <button
          onClick={onNext}
          disabled={currentPage === totalPages}
          style={{
            padding: '6px 12px',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            background: '#fff',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontFamily: "'Book Antiqua', 'Times New Roman', serif",
            color: '#475569',
            transition: 'all 0.2s',
            opacity: currentPage === totalPages ? 0.5 : 1
          }}
        >Next &raquo;</button>
      </div>
      
      <div style={{ fontSize: '13px', color: '#5b677b', fontFamily: "'Book Antiqua', 'Times New Roman', serif" }}>
        Page {currentPage} of {totalPages}
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: EmptyState
// ============================================================

const EmptyState = ({ onClearFilters }) => {
  return (
    <div style={{
      textAlign: 'center',
      padding: '60px',
      color: '#8a99b0',
      fontFamily: "'Book Antiqua', 'Times New Roman', serif"
    }}>
      <span style={{ fontSize: '48px', opacity: 0.5, display: 'block', marginBottom: '16px' }}>📭</span>
      <p>No invoices found</p>
      <button
        style={{
          marginTop: '16px',
          padding: '8px 20px',
          borderRadius: '20px',
          border: '1px solid #e2e8f0',
          background: '#fff',
          cursor: 'pointer',
          fontSize: '13px',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif"
        }}
        onClick={onClearFilters}
      >Clear filters</button>
    </div>
  );
};

// ============================================================
// COMPONENT: LoadingState
// ============================================================

const LoadingState = () => {
  return (
    <div style={{
      textAlign: 'center',
      padding: '60px',
      color: '#8a99b0',
      fontFamily: "'Book Antiqua', 'Times New Roman', serif"
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTopColor: '#1b4d3e',
        borderRadius: '50%',
        margin: '0 auto 16px',
        animation: 'spin 0.8s linear infinite'
      }}></div>
      <p>Loading invoices...</p>
    </div>
  );
};

// ============================================================
// COMPONENT: TransactionsHeader
// ============================================================

const TransactionsHeader = ({ selectedBranch, onBranchChange, branches }) => {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '16px'
    }}>
      <div style={{ flex: 1 }}>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          fontFamily: "'Book Antiqua', 'Times New Roman', serif",
          color: '#475569',
          margin: 0,
          letterSpacing: '0.5px'
        }}>Transactions</h1>
        <p style={{
          fontSize: '13px',
          color: '#8a99b0',
          marginTop: '4px',
          marginBottom: 0,
          fontFamily: "'Book Antiqua', 'Times New Roman', serif"
        }}>Invoice history & billing records</p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <BranchSelector
          currentBranch={selectedBranch}
          onBranchChange={onBranchChange}
          branches={branches}
        />
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: FilterBar
// ============================================================

const FilterBar = ({
  searchTerm,
  setSearchTerm,
  dateFilter,
  setDateFilter,
  showDateDropdown,
  setShowDateDropdown,
  dateDropdownRef,
  paymentFilter,
  setPaymentFilter,
  showPaymentDropdown,
  setShowPaymentDropdown,
  paymentDropdownRef,
  deliveryStatusFilter,
  setDeliveryStatusFilter,
  showStatusDropdown,
  setShowStatusDropdown,
  statusDropdownRef,
  deliveryTypeFilter,
  setDeliveryTypeFilter,
  showTypeDropdown,
  setShowTypeDropdown,
  typeDropdownRef,
  showCustomDatePicker,
  customDateRange,
  setCustomDateRange,
  onExport,
  exporting,
  filteredCount,
  onClearFilters,
  dateOptions,
  paymentOptions,
  statusFilterOptions,
  deliveryTypeOptions
}) => {
  return (
    <div style={{
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
      
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
        <FilterDropdown
          icon="📅"
          label="Date"
          options={dateOptions}
          value={dateFilter}
          onChange={(value) => {
            setDateFilter(value);
            // This will be handled by parent
          }}
          dropdownRef={dateDropdownRef}
          showDropdown={showDateDropdown}
          setShowDropdown={setShowDateDropdown}
        />
        
        {showCustomDatePicker && (
          <CustomDateRangePicker
            from={customDateRange.from}
            to={customDateRange.to}
            onFromChange={(value) => setCustomDateRange({ ...customDateRange, from: value })}
            onToChange={(value) => setCustomDateRange({ ...customDateRange, to: value })}
          />
        )}
        
        <FilterDropdown
          icon="💳"
          label="Payment"
          options={paymentOptions}
          value={paymentFilter}
          onChange={setPaymentFilter}
          dropdownRef={paymentDropdownRef}
          showDropdown={showPaymentDropdown}
          setShowDropdown={setShowPaymentDropdown}
        />
        
        <FilterDropdown
          icon="🚚"
          label="Status"
          options={statusFilterOptions}
          value={deliveryStatusFilter}
          onChange={setDeliveryStatusFilter}
          dropdownRef={statusDropdownRef}
          showDropdown={showStatusDropdown}
          setShowDropdown={setShowStatusDropdown}
        />
        
        <FilterDropdown
          icon="🏪"
          label="Type"
          options={deliveryTypeOptions}
          value={deliveryTypeFilter}
          onChange={setDeliveryTypeFilter}
          dropdownRef={typeDropdownRef}
          showDropdown={showTypeDropdown}
          setShowDropdown={setShowTypeDropdown}
        />
        
        <button
          style={{
            padding: '10px 16px',
            border: '1px solid #e2e8f0',
            borderRadius: '10px',
            background: '#fff',
            cursor: 'pointer',
            fontSize: '13px',
            fontFamily: "'Book Antiqua', 'Times New Roman', serif",
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            opacity: exporting || filteredCount === 0 ? 0.6 : 1
          }}
          onClick={onExport}
          disabled={exporting || filteredCount === 0}
        >
          {exporting ? '⏳' : <FaFileExcel color="#1D6F42" size={18} />}
          <span>{exporting ? 'Exporting...' : 'Export'}</span>
        </button>
        
        {(searchTerm || dateFilter !== 'all' || paymentFilter !== 'all' || deliveryStatusFilter !== 'all' || deliveryTypeFilter !== 'all') && (
          <button
            style={{
              padding: '10px 16px',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              background: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#ef4444',
              fontWeight: 500
            }}
            onClick={onClearFilters}
          >✕ Clear</button>
        )}
      </div>
    </div>
  );
};

// ============================================================
// COMPONENT: TransactionTable
// ============================================================

const TransactionTable = ({
  bills,
  onPrintThermal,
  onPrintA4,
  onStatusChange,
  updatingStatus,
  getStatusStyle
}) => {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        minWidth: '800px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif"
      }}>
        <thead>
          <tr style={{
            background: '#f8fafd',
            borderBottom: '1px solid #e9edf2'
          }}>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Invoice</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Date & Time</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Customer</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'right',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Amount</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Payment</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Delivery</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'left',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Status</th>
            <th style={{
              padding: '14px 16px',
              textAlign: 'center',
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: "'Book Antiqua', 'Times New Roman', serif",
              color: '#5b677b',
              letterSpacing: '0.3px'
            }}>Print</th>
          </tr>
        </thead>
        <tbody>
          {bills.map(bill => (
            <TransactionRow
              key={bill.bill_id}
              bill={bill}
              onPrintThermal={onPrintThermal}
              onPrintA4={onPrintA4}
              onStatusChange={onStatusChange}
              updatingStatus={updatingStatus}
              getStatusStyle={getStatusStyle}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================
// MAIN TRANSACTIONS COMPONENT
// ============================================================

function Transactions() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState('all');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('all');
  const [printing, setPrinting] = useState(false);
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [itemsPerPageOptions] = useState([10, 25, 50, 100]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const dateDropdownRef = useRef(null);
  const paymentDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const typeDropdownRef = useRef(null);

  // Date options
  const dateOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const paymentOptions = [
    { value: 'all', label: 'All Payments' },
    { value: 'Cash', label: 'Cash' },
    { value: 'Card', label: 'Card' },
    { value: 'Other', label: 'Other' },
    { value: 'Bank Transfer', label: 'Bank Transfer' }
  ];

  const statusFilterOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'Delivery Pending', label: 'Delivery Pending' },
    { value: 'Out for Delivery', label: 'Out for Delivery' },
    { value: 'Delivered', label: 'Delivered' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  const deliveryTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'walkin', label: ' Walk-in' },
    { value: 'delivery', label: ' Delivery' }
  ];

  // Fetch branches on mount
  useEffect(() => {
    fetchBranches();
  }, []);

  // Fetch bills when branch changes
  useEffect(() => {
    if (selectedBranch) {
      fetchBills();
      setCurrentPage(1);
    }
  }, [selectedBranch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter, paymentFilter, deliveryStatusFilter, deliveryTypeFilter, customDateRange]);

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (paymentDropdownRef.current && !paymentDropdownRef.current.contains(event.target)) {
        setShowPaymentDropdown(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // API Functions
  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_URL}/branches`);
      setBranches(response.data);
      if (response.data.length > 0) {
        setSelectedBranch(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBills = async () => {
    try {
      setLoading(true);
      const branchId = selectedBranch?.id || 1;
      const response = await axios.get(`${API_URL}/transactions/bills`, {
        headers: { 'x-branch-id': branchId }
      });
      setBills(response.data);
    } catch (error) {
      console.error('Error fetching bills:', error);
      alert('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBillDetails = async (billId) => {
    try {
      const response = await axios.get(`${API_URL}/transactions/bills/${billId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bill details:', error);
      return null;
    }
  };

  // Update delivery status
  const updateDeliveryStatus = async (billId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(`${API_URL}/transactions/bills/${billId}/delivery-status`, {
        delivery_status: newStatus
      });
      setBills(prevBills => 
        prevBills.map(bill => 
          bill.bill_id === billId ? { ...bill, delivery_status: newStatus } : bill
        )
      );
      alert(`✅ Status updated to: ${newStatus}`);
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Print bill function
  const printBill = (type, billData) => {
    if (!billData) {
      alert('No bill to print');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    const printContent = type === 'thermal'
      ? React.createElement(ThermalBillPrint, { 
          bill: billData, 
          branch: selectedBranch, 
          vatRate: 5, 
          deliveryType: billData.delivery_type 
        })
      : React.createElement(A4BillPrint, { 
          bill: billData, 
          branch: selectedBranch, 
          vatRate: 5, 
          deliveryType: billData.delivery_type 
        });

    const container = document.createElement('div');
    const root = ReactDOM.createRoot(container);
    root.render(printContent);

    setTimeout(() => {
      const html = container.innerHTML;
      
      const thermalStyles = `
        @media print {
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 80mm !important;
            background: white;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          @page { 
            size: 80mm auto; 
            margin: 0mm !important;
          }
          * {
            color: #000000 !important;
            font-family: monospace !important;
          }
          .bill-logo {
            filter: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        
        * {
          font-family: monospace;
          box-sizing: border-box;
        }
      `;

      const a4Styles = `
        @media print {
          body { 
            margin: 20px; 
            padding: 0; 
            background: white;
          }
          @page { 
            size: A4; 
            margin: 20mm;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `;

      const printStyles = type === 'thermal' ? thermalStyles : a4Styles;

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Invoice - ${billData.bill_number || 'Bill'}</title>
            <style>
              ${printStyles}
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() {
                    window.close();
                  }, 500);
                }, 300);
              };
            <\/script>
          </body>
        </html>
      `);
      printWindow.document.close();
      
      setTimeout(() => {
        root.unmount();
      }, 1000);
    }, 200);
  };

  // Print thermal bill
  const printThermalBill = async (bill) => {
    setPrinting(true);
    try {
      let billWithItems = bill;
      if (!bill.items || bill.items.length === 0) {
        billWithItems = await fetchBillDetails(bill.bill_id);
      }
      if (billWithItems) {
        printBill('thermal', billWithItems);
      } else {
        alert('Failed to load bill details');
      }
    } catch (error) {
      console.error('Error printing:', error);
      alert('Failed to print bill');
    } finally {
      setPrinting(false);
    }
  };

  // Print A4 bill
  const printA4Bill = async (bill) => {
    setPrinting(true);
    try {
      let billWithItems = bill;
      if (!bill.items || bill.items.length === 0) {
        billWithItems = await fetchBillDetails(bill.bill_id);
      }
      if (billWithItems) {
        printBill('a4', billWithItems);
      } else {
        alert('Failed to load bill details');
      }
    } catch (error) {
      console.error('Error printing:', error);
      alert('Failed to print bill');
    } finally {
      setPrinting(false);
    }
  };

  // Filter bills
  const filterByDate = (billDate) => {
    const billDateObj = new Date(billDate);
    billDateObj.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
      case 'today':
        return billDateObj.toDateString() === today.toDateString();
      case 'week':
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        return billDateObj >= weekAgo;
      case 'month':
        const monthAgo = new Date(today);
        monthAgo.setMonth(today.getMonth() - 1);
        return billDateObj >= monthAgo;
      case 'custom':
        if (customDateRange.from && customDateRange.to) {
          const fromDate = new Date(customDateRange.from);
          fromDate.setHours(0, 0, 0, 0);
          const toDate = new Date(customDateRange.to);
          toDate.setHours(23, 59, 59, 999);
          return billDateObj >= fromDate && billDateObj <= toDate;
        }
        return true;
      default:
        return true;
    }
  };

  const filteredBills = bills.filter(bill => {
    const matchesSearch = searchTerm === '' ? true : 
      bill.bill_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (bill.customer_phone && bill.customer_phone.includes(searchTerm));
    const matchesDate = filterByDate(bill.created_at);
    const matchesPayment = paymentFilter === 'all' ? true : bill.payment_mode === paymentFilter;
    const matchesStatus = deliveryStatusFilter === 'all' ? true : bill.delivery_status === deliveryStatusFilter;
    const matchesDeliveryType = deliveryTypeFilter === 'all' ? true : bill.delivery_type === deliveryTypeFilter;
    return matchesSearch && matchesDate && matchesPayment && matchesStatus && matchesDeliveryType;
  });

  // Pagination
  const totalItems = filteredBills.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentBills = filteredBills.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    return pageNumbers;
  };

  // Export to Excel
  const exportToExcel = async () => {
    try {
      setExporting(true);
      const excelData = [];
      filteredBills.forEach((bill, index) => {
        excelData.push({
          'S.No': index + 1,
          'Invoice No': bill.bill_number,
          'Date': new Date(bill.created_at).toLocaleDateString(),
          'Time': new Date(bill.created_at).toLocaleTimeString(),
          'Customer Name': bill.customer_name,
          'Customer Phone': bill.customer_phone || '-',
          'Payment Mode': bill.payment_mode,
          'Delivery Type': bill.delivery_type === 'delivery' ? 'Home Delivery' : 'Walk-in',
          'Delivery Status': bill.delivery_status || 'Completed',
          'Branch': bill.branch_name || selectedBranch?.branch_name || '-',
          'Total Amount (AED)': parseFloat(bill.final_amount).toFixed(2)
        });
      });
      const totalGrand = filteredBills.reduce((sum, bill) => sum + parseFloat(bill.final_amount), 0);
      excelData.push({});
      excelData.push({ 'Total Amount (AED)': `GRAND TOTAL: ${totalGrand.toFixed(2)}` });
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
      const fileName = `Transactions_${selectedBranch?.branch_name || 'All'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      alert(`✅ Exported ${filteredBills.length} invoices to Excel!`);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('all');
    setPaymentFilter('all');
    setDeliveryStatusFilter('all');
    setDeliveryTypeFilter('all');
    setCustomDateRange({ from: '', to: '' });
    setShowCustomDatePicker(false);
    setCurrentPage(1);
  };

  const handleBranchChange = (branch) => {
    setSelectedBranch(branch);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    setShowCustomDatePicker(value === 'custom');
  };

  return (
    <div style={{
      minHeight: '100vh',
      fontFamily: "'Book Antiqua', 'Times New Roman', serif",
      padding: '20px',
      background: '#f8fafc'
    }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      
      {/* Header */}
      <TransactionsHeader
        selectedBranch={selectedBranch}
        onBranchChange={handleBranchChange}
        branches={branches}
      />

      {/* Stats Bar */}
      <StatsBar
        branch={selectedBranch}
        totalInvoices={filteredBills.length}
      />

      {/* Filter Bar */}
      <FilterBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        dateFilter={dateFilter}
        setDateFilter={handleDateFilterChange}
        showDateDropdown={showDateDropdown}
        setShowDateDropdown={setShowDateDropdown}
        dateDropdownRef={dateDropdownRef}
        paymentFilter={paymentFilter}
        setPaymentFilter={setPaymentFilter}
        showPaymentDropdown={showPaymentDropdown}
        setShowPaymentDropdown={setShowPaymentDropdown}
        paymentDropdownRef={paymentDropdownRef}
        deliveryStatusFilter={deliveryStatusFilter}
        setDeliveryStatusFilter={setDeliveryStatusFilter}
        showStatusDropdown={showStatusDropdown}
        setShowStatusDropdown={setShowStatusDropdown}
        statusDropdownRef={statusDropdownRef}
        deliveryTypeFilter={deliveryTypeFilter}
        setDeliveryTypeFilter={setDeliveryTypeFilter}
        showTypeDropdown={showTypeDropdown}
        setShowTypeDropdown={setShowTypeDropdown}
        typeDropdownRef={typeDropdownRef}
        showCustomDatePicker={showCustomDatePicker}
        customDateRange={customDateRange}
        setCustomDateRange={setCustomDateRange}
        onExport={exportToExcel}
        exporting={exporting}
        filteredCount={filteredBills.length}
        onClearFilters={clearFilters}
        dateOptions={dateOptions}
        paymentOptions={paymentOptions}
        statusFilterOptions={statusFilterOptions}
        deliveryTypeOptions={deliveryTypeOptions}
      />

      {/* Table */}
      <div style={{
        background: '#fff',
        borderRadius: '14px',
        border: '1px solid #e9edf2',
        overflow: 'hidden'
      }}>
        {loading ? (
          <LoadingState />
        ) : filteredBills.length === 0 ? (
          <EmptyState onClearFilters={clearFilters} />
        ) : (
          <>
            <TransactionTable
              bills={currentBills}
              onPrintThermal={printThermalBill}
              onPrintA4={printA4Bill}
              onStatusChange={updateDeliveryStatus}
              updatingStatus={updatingStatus}
              getStatusStyle={getStatusStyle}
            />
            
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                itemsPerPageOptions={itemsPerPageOptions}
                onPageChange={goToPage}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPrevious={goToPreviousPage}
                onNext={goToNextPage}
                getPageNumbers={getPageNumbers}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Transactions;
